import axios from "axios";
import config from "../config/config";
import store from "../store";
import { startLoading, stopLoading } from "../store/slices/commonLoaderSlice";
import { isGuestUser } from "../utils/guestUser";


const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

/** Detect admin routes reliably (path alone or baseURL + path; avoids missing token / wrong 401 branch). */
function isAdminApiRequest(cfg) {
  if (!cfg) return false;
  const p = cfg.url || "";
  if (p.startsWith("/admin") || p.startsWith("admin/")) return true;
  const base = cfg.baseURL || "";
  const combined = `${base}${p}`;
  return /\/admin(\/|$)/.test(combined);
}

// Attach token from localStorage to outgoing requests
api.interceptors.request.use((cfg) => {
  try {
    // decide which token to use based on request URL
    const isAdminRequest = isAdminApiRequest(cfg);
    const token = isAdminRequest
      ? localStorage.getItem("admintoken") || localStorage.getItem("mastertoken")
      : localStorage.getItem("token");

    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }

    // Add cache-busting parameter for admin requests to prevent CORS caching issues
    if (isAdminRequest && cfg.method?.toLowerCase() === 'get') {
      cfg.params = {
        ...cfg.params,
        _t: Date.now() // cache-buster parameter
      };
    }

    store.dispatch(startLoading())
  } catch (e) {
    // ignore
  }
  return cfg;
});

// Global response handler
api.interceptors.response.use(
  (resp) =>{
    store.dispatch(stopLoading());
    return resp
  },
  (err) => {
    try {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        const skipRedirect = err?.config?.skipAuthRedirect;
        if (skipRedirect) {
          // let the caller handle the error
          return Promise.reject(err);
        }
        const isAdminRequest = isAdminApiRequest(err?.config);

        if (isAdminRequest) {
          localStorage.removeItem("admintoken");
          localStorage.removeItem("adminUser");
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
        } else {
          // Read `user` BEFORE clearing it — a genuinely expired guest
          // token (the proactive hard-limit timer in AppLayout.js missed
          // its window, e.g. a throttled background tab or laptop sleep)
          // should still land on the friendly /demo-ended screen, not the
          // real login form a guest has no credentials for. This is a full
          // page reload (window.location.href), not a router navigate, so
          // the reason travels as a query param rather than router state —
          // DemoEnded reads both.
          let wasGuest = false;
          try {
            const storedUser = localStorage.getItem("user");
            wasGuest = isGuestUser(storedUser ? JSON.parse(storedUser) : null);
          } catch {
            // ignore
          }

          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (typeof window !== "undefined") {
            window.location.href = wasGuest ? "/demo-ended?reason=timeout" : "/login";
          }
        }
      }
    } catch (e) {
      // ignore
    } finally {
      store.dispatch(stopLoading());
    }
    return Promise.reject(err);
  }
);


export default api;
